import { Suspense, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { ResendVerification } from "@/components/resend-verification";
import { useLanguage } from "@/hooks/use-language";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

export const Route = createFileRoute("/_auth/verify-email")({
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  component: VerifyEmailPage,
});

const API_BASE =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

type VerifyState = "loading" | "success" | "error";

function VerifyEmailContent() {
  const { token } = Route.useSearch();
  const { t } = useLanguage();

  const [state, setState] = useState<VerifyState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMessage(t("auth.verify_no_token"));
      return;
    }

    let cancelled = false;
    async function verifyEmail() {
      try {
        const res = await fetch(`${API_BASE}/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const body = await res.json();

        if (cancelled) return;
        if (body.success) {
          setState("success");
        } else {
          setState("error");
          setErrorMessage(body.error || t("auth.verify_fail"));
        }
      } catch {
        if (cancelled) return;
        setState("error");
        setErrorMessage(t("auth.verify_error"));
      }
    }

    void verifyEmail();
    return () => { cancelled = true; };
  }, [token, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t("auth.verify_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {state === "loading" && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">
              {t("auth.verify_loading")}
            </p>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("auth.verify_success")}
              <br />
              {t("auth.verify_success_desc")}
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </div>
            <p className="text-sm text-destructive">{errorMessage}</p>
          </div>
        )}
        {state === "error" && <ResendVerification />}
      </CardContent>
      <CardFooter className="justify-center">
        <Link to="/login">
          <Button variant="outline" size="lg">
            {t("auth.verify_go_login")}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

function VerifyEmailPage() {
  return (
    <Suspense
      fallback={
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </CardContent>
        </Card>
      }
    >
      <VerifyEmailContent />
    </Suspense>
  );
}
